'use client'

import { useEffect, useRef } from 'react'

/**
 * PNG firework bursts over the 250th hero. Ported from the Claude Design canvas:
 * each burst is an <img> placed at a random spot in the upper hero, animated once
 * (scale-in + fade via the `america250-fw-burst` keyframe in globals.css), then
 * removed. New bursts avoid overlapping live ones. Spawning pauses when the hero
 * scrolls offscreen and is skipped entirely under `prefers-reduced-motion`.
 *
 * Renders an absolutely-positioned, pointer-events-none layer that fills its
 * (relatively-positioned) parent hero section.
 */

const FW_SRCS = [
    '/next-static/campaigns/america-250/fireworks/fw1.png',
    '/next-static/campaigns/america-250/fireworks/fw2.png',
    '/next-static/campaigns/america-250/fireworks/fw5.png',
    '/next-static/campaigns/america-250/fireworks/fw7.png',
]

export function FireworksOverlay() {
    const layerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const layer = layerRef.current
        if (!layer) {
            return
        }
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return
        }

        let timer: ReturnType<typeof setTimeout> | null = null
        let lastSrcIdx = -1
        // Live bursts, tracked as {x, y, r} so new bursts never overlap one.
        const active: { x: number; y: number; r: number }[] = []
        const rand = (min: number, max: number) => min + Math.random() * (max - min)

        const spawn = () => {
            const w = layer.clientWidth
            const h = layer.clientHeight
            if (!w || !h) {
                return
            }
            const size = rand(150, 250)
            const r = size / 2
            const minX = r
            const maxX = w - r
            const minY = r * 0.7
            const maxY = Math.max(minY + 1, h * 0.66)
            if (maxX <= minX) {
                return
            }

            // Try several spots; reject any that would overlap a live burst (with a gap).
            let x: number | null = null
            let y: number | null = null
            for (let i = 0; i < 16; i++) {
                const cx = rand(minX, maxX)
                const cy = rand(minY, maxY)
                const clash = active.some((a) => Math.hypot(cx - a.x, cy - a.y) < r + a.r + 24)
                if (!clash) {
                    x = cx
                    y = cy
                    break
                }
            }
            if (x === null || y === null) {
                return // sky's full right now — skip this one
            }

            const item = { x, y, r }
            active.push(item)

            // Pick a design that isn't the one we just used.
            let srcIdx = Math.floor(Math.random() * FW_SRCS.length)
            if (FW_SRCS.length > 1 && srcIdx === lastSrcIdx) {
                srcIdx =
                    (srcIdx + 1 + Math.floor(Math.random() * (FW_SRCS.length - 1))) % FW_SRCS.length
            }
            lastSrcIdx = srcIdx

            const img = document.createElement('img')
            img.src = FW_SRCS[srcIdx]
            img.alt = ''
            const dur = rand(1500, 2200)
            img.style.cssText =
                `position:absolute;left:${x}px;top:${y}px;width:${size}px;height:auto;` +
                `will-change:transform,opacity;` +
                `animation:america250-fw-burst ${dur}ms cubic-bezier(.15,.6,.3,1) forwards;`

            const cleanup = () => {
                const idx = active.indexOf(item)
                if (idx !== -1) {
                    active.splice(idx, 1)
                }
                img.remove()
            }
            img.addEventListener('animationend', cleanup)
            // Safety net in case animationend doesn't fire.
            window.setTimeout(cleanup, dur + 200)
            layer.appendChild(img)
        }

        const start = () => {
            if (timer) {
                return
            }
            const tick = () => {
                spawn()
                timer = setTimeout(tick, rand(1100, 1900))
            }
            spawn()
            timer = setTimeout(tick, 1300)
        }

        const stop = () => {
            if (timer) {
                clearTimeout(timer)
            }
            timer = null
        }

        // Pause spawning while the hero is offscreen.
        let observer: IntersectionObserver | null = null
        if ('IntersectionObserver' in window) {
            observer = new IntersectionObserver(
                (entries) => {
                    const entry = entries[0]
                    if (!entry) {
                        return
                    }
                    if (entry.isIntersecting) {
                        start()
                    } else {
                        stop()
                    }
                },
                { threshold: 0.01 },
            )
            observer.observe(layer)
        } else {
            start()
        }

        return () => {
            stop()
            observer?.disconnect()
            layer.replaceChildren()
        }
    }, [])

    return (
        <div
            ref={layerRef}
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
        />
    )
}
